import { ReactNode, useId, useRef } from 'react';
import styled from '@emotion/styled';
import {
	AccordionTitle,
	ChevronDownIconLight,
	Css,
	ITheme,
	Markdown,
	Responsive,
	StyledAccordionTitle,
	themed,
	useControlledState,
} from '@/ui';
import { Color } from '../../../styles/Color';

export interface IAccordionItemProps {
	title: string | ReactNode;
	subtitle?: ReactNode;
	body?: string;
	setIsExpanded?: (val: boolean) => void;
	isExpanded?: boolean;
	className?: string;
	icon?: ReactNode;
	withTitleHighlight?: boolean;
	reverseIcon?: boolean;
	children?: ReactNode;
}

export function AccordionItem({
	title,
	subtitle,
	body,
	setIsExpanded: setIsExpandedProp,
	isExpanded: isExpandedProp,
	className,
	icon,
	withTitleHighlight = true,
	reverseIcon = false,
	children,
}: IAccordionItemProps) {
	const accordionTitleId = useId();
	const accordionContentId = useId();
	const ref = useRef<HTMLDivElement>(null);
	const [isExpanded, setIsExpanded] = useControlledState(
		isExpandedProp ?? false,
		isExpandedProp,
		setIsExpandedProp,
	);

	const handleToggle = () => {
		if (!children && !body) return;
		setIsExpanded(!isExpanded);
	};

	return (
		<AccordionItemStyled className={className}>
			<AccordionTitle
				onClick={handleToggle}
				isActive={isExpanded && withTitleHighlight}
				title={title}
				icon={icon}
				iconDown={<ChevronDownIconLight />}
				reverseIcon={reverseIcon && isExpanded}
				aria-controls={accordionContentId}
				id={accordionTitleId}
			/>
			{subtitle ? <div>{subtitle}</div> : null}

			<StyledAccordionWrapper
				id={accordionContentId}
				ref={ref}
				isVisible={isExpanded}
				role="region"
				contentHeight={ref.current?.scrollHeight || 0}
				aria-hidden={!isExpanded}
				aria-labelledby={accordionTitleId}
			>
				<StyledContentWrap>
					{children || <Markdown source={body as string} />}
				</StyledContentWrap>
			</StyledAccordionWrapper>
		</AccordionItemStyled>
	);
}

const AccordionItemStyled = styled.div<{}, ITheme>`
	border-bottom: 1px solid ${Color.Neutral80};

	svg {
		width: ${Css.pxToRem(26)};
		height: ${Css.pxToRem(26)};

		path {
			fill: ${({ theme }) => theme.color.brand};
		}
	}

	${StyledAccordionTitle} {
		padding: ${themed(({ accordion }) => accordion.title.padding)};
		font-weight: 700;
		text-align: left;
	}
`;

export const StyledAccordionWrapper = styled.div<{
	isVisible: boolean;
	contentHeight?: number;
}>`
	max-height: ${({ isVisible, contentHeight }) =>
		isVisible ? `${contentHeight}px` : '0'};
	transition: max-height 0.5s cubic-bezier(0.65, 0, 0.35, 1);
	overflow: hidden;
	visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
`;

const StyledContentWrap = styled.div`
	width: 50%;
	margin: 0 0 ${Css.pxToRem(22)};

	ul,
	ol {
		margin-top: 0;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100%;
	}
`;
